import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';
import web3Provider from '../utils/web3Provider';
import { TOKENS } from '../config/constantsV2';

const FOUNTAIN_ADDRESS = '0xb23ac6cbadef580c4825f128763f6c7ddaa0e0ce';
const LSHARE_OLD_TOKEN = TOKENS.LSHARE_OLD;

const FOUNTAIN_ABI = [
  'function balanceOf(address account) external view returns (uint256)',
  'function stake(uint256 amount) external',
  'function withdraw(uint256 amount) external',
  'function share() external view returns (address)',
  'function earned(address mason) external view returns (uint256)',
  'function depositFee() external view returns (uint256)',
  'function canWithdraw(address mason) external view returns (bool)',
  'function canClaimReward(address mason) external view returns (bool)',
  'function treasury() external view returns (address)',
  'function withdrawLockupEpochs() external view returns (uint256)',
  'function rewardLockupEpochs() external view returns (uint256)',
  'event Staked(address indexed user, uint256 amount, uint256 depositFeeAmount)',
  'event Withdrawn(address indexed user, uint256 amount)',
];

const TREASURY_ABI = [
  'function currentEpoch() external view returns (uint256)',
  'function nextEpochPoint() external view returns (uint256)',
];

const ERC20_ABI = [
  'function balanceOf(address owner) external view returns (uint256)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function symbol() external view returns (string)',
  'function decimals() external view returns (uint8)',
];

export const useFountain = () => {
  const [stakedBalance, setStakedBalance] = useState('0');
  const [walletBalance, setWalletBalance] = useState('0');
  const [earnedRewards, setEarnedRewards] = useState('0');
  const [depositToken] = useState(LSHARE_OLD_TOKEN);
  const [depositFee, setDepositFee] = useState('0');
  const [isLoading, setIsLoading] = useState(false);
  const [canWithdrawStatus, setCanWithdrawStatus] = useState(false);
  const [lockupInfo, setLockupInfo] = useState(null);

  const fetchBalances = useCallback(async (account) => {
    if (!account) return;

    try {
      const provider = web3Provider.getProvider();
      const fountain = new ethers.Contract(FOUNTAIN_ADDRESS, FOUNTAIN_ABI, provider);

      // Get staked balance
      const staked = await fountain.balanceOf(account);
      setStakedBalance(ethers.formatEther(staked));

      // Get earned rewards
      const earned = await fountain.earned(account);
      setEarnedRewards(ethers.formatEther(earned));

      // Get wallet balance of LSHARE OLD
      const shareToken = new ethers.Contract(LSHARE_OLD_TOKEN.address, ERC20_ABI, provider);
      const wallet = await shareToken.balanceOf(account);
      setWalletBalance(ethers.formatUnits(wallet, LSHARE_OLD_TOKEN.decimals));

      // Get deposit fee
      const fee = await fountain.depositFee();
      setDepositFee((Number(fee) / 100).toFixed(2));

      // Check if user can withdraw
      const canWithdraw = await fountain.canWithdraw(account);
      setCanWithdrawStatus(canWithdraw);

      // Get lockup info
      try {
        const treasuryAddress = await fountain.treasury();
        const treasury = new ethers.Contract(treasuryAddress, TREASURY_ABI, provider);
        const currentEpoch = await treasury.currentEpoch();
        const nextEpochPoint = await treasury.nextEpochPoint();
        const withdrawLockup = await fountain.withdrawLockupEpochs();
        const rewardLockup = await fountain.rewardLockupEpochs();
        
        setLockupInfo({
          currentEpoch: Number(currentEpoch),
          nextEpochPoint: Number(nextEpochPoint),
          withdrawLockupEpochs: Number(withdrawLockup),
          rewardLockupEpochs: Number(rewardLockup),
        });
      } catch (lockupError) {
        console.error('Error fetching lockup info:', lockupError);
      }

    } catch (error) {
      console.error('Error fetching balances:', error);
    }
  }, []);

  const deposit = useCallback(async (amount) => {
    if (!web3Provider.isConnected()) {
      throw new Error('Wallet not connected');
    }

    setIsLoading(true);
    const toastId = toast.loading('Preparing deposit...');

    try {
      const signer = web3Provider.getSigner();
      const account = web3Provider.getAccount();
      const fountain = new ethers.Contract(FOUNTAIN_ADDRESS, FOUNTAIN_ABI, signer);
      const shareToken = new ethers.Contract(LSHARE_OLD_TOKEN.address, ERC20_ABI, signer);

      const amountWei = ethers.parseUnits(amount, LSHARE_OLD_TOKEN.decimals);

      // Check allowance
      toast.loading('Checking token allowance...', { id: toastId });
      const allowance = await shareToken.allowance(account, FOUNTAIN_ADDRESS);

      if (allowance < amountWei) {
        toast.loading('Approving tokens...', { id: toastId });
        const approveTx = await shareToken.approve(FOUNTAIN_ADDRESS, ethers.MaxUint256);
        toast.loading('Waiting for approval confirmation...', { id: toastId });
        await approveTx.wait();
      }

      // Stake
      toast.loading('Depositing tokens...', { id: toastId });
      const tx = await fountain.stake(amountWei);
      
      toast.loading('Waiting for confirmation...', { id: toastId });
      const receipt = await tx.wait();

      toast.success(
        <div>
          <div className="font-bold">Deposit Successful!</div>
          <div className="text-sm">
            Deposited {amount} {LSHARE_OLD_TOKEN.symbol}
          </div>
        </div>,
        { id: toastId, duration: 5000 }
      );

      // Refresh balances
      await fetchBalances(account);

      return receipt;
    } catch (error) {
      console.error('Deposit error:', error);
      
      // Filter out technical errors from user-facing messages
      let errorMessage = 'Deposit failed';
      if (error.code === 'ACTION_REJECTED') {
        errorMessage = 'Transaction rejected';
      } else if (error.message?.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds';
      } else if (error.message?.includes('allowance')) {
        errorMessage = 'Token approval failed';
      }
      // Don't show raw RPC/JSON errors to users
      
      toast.error(errorMessage, { id: toastId });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [fetchBalances]);

  const withdraw = useCallback(async (amount) => {
    if (!web3Provider.isConnected()) {
      throw new Error('Wallet not connected');
    }

    setIsLoading(true);
    const toastId = toast.loading('Preparing withdrawal...');

    try {
      const signer = web3Provider.getSigner();
      const account = web3Provider.getAccount();
      const fountain = new ethers.Contract(FOUNTAIN_ADDRESS, FOUNTAIN_ABI, signer);

      const amountWei = ethers.parseUnits(amount, LSHARE_OLD_TOKEN.decimals);

      toast.loading('Withdrawing tokens...', { id: toastId });
      const tx = await fountain.withdraw(amountWei);
      
      toast.loading('Waiting for confirmation...', { id: toastId });
      
      let receipt;
      try {
        receipt = await tx.wait();
      } catch (receiptError) {
        // Handle RPC returning null receipt even though transaction succeeded
        if (receiptError.code === 'BAD_DATA' && tx.hash) {
          console.log('Transaction sent but receipt returned null. Hash:', tx.hash);
          // Transaction was sent successfully, just wait a bit and refresh
          await new Promise(resolve => setTimeout(resolve, 3000));
        } else {
          throw receiptError;
        }
      }

      toast.success(
        <div>
          <div className="font-bold">Withdrawal Successful!</div>
          <div className="text-sm">
            Withdrew {amount} {LSHARE_OLD_TOKEN.symbol}
          </div>
        </div>,
        { id: toastId, duration: 5000 }
      );

      // Refresh balances
      await fetchBalances(account);

      return receipt || tx;
    } catch (error) {
      console.error('Withdrawal error:', error);
      
      // Filter out technical errors from user-facing messages
      let errorMessage = 'Withdrawal failed';
      if (error.code === 'ACTION_REJECTED') {
        errorMessage = 'Transaction rejected';
      } else if (error.message?.includes('still in withdraw lockup')) {
        errorMessage = 'Still in lockup period - please wait';
      } else if (error.message?.includes('lockup')) {
        errorMessage = 'Tokens are still locked';
      } else if (error.message?.includes('insufficient')) {
        errorMessage = 'Insufficient staked balance';
      }
      // Don't show raw RPC/JSON errors to users
      
      toast.error(errorMessage, { id: toastId });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [fetchBalances]);

  return {
    stakedBalance,
    walletBalance,
    earnedRewards,
    depositToken,
    depositFee,
    isLoading,
    canWithdrawStatus,
    lockupInfo,
    fetchBalances,
    deposit,
    withdraw,
  };
};
